import {ArrowDownIcon, ArrowUpIcon} from "lucide-react";

import {Card, CardContent} from "@components/ui/card";

import type {XeroPnLKpiCard} from "@hooks/use-vnext";

function formatValue(card: XeroPnLKpiCard): string {
    if (card.value == null) return "N/A";
    if (card.key.endsWith("_pct") || card.key === "opex_ratio") {
        return `${card.value}%`;
    }
    const abs = Math.abs(card.value);
    if (abs >= 1_000_000) return `$${(card.value / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `$${(card.value / 1_000).toFixed(0)}K`;
    return `$${card.value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function formatDollar(v: number): string {
    const abs = Math.abs(v);
    const formatted = abs >= 1_000_000
        ? `$${(abs / 1_000_000).toFixed(1)}M`
        : abs >= 1_000
          ? `$${(abs / 1_000).toFixed(0)}K`
          : `$${abs.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
    return v >= 0 ? `+${formatted}` : `-${formatted}`;
}

const INVERTED_KEYS = new Set([
    "cogs", "operating_expenses", "opex", "opex_ratio",
    "total_expenses", "ar_overdue", "overdue",
]);

function isUpBad(key: string): boolean {
    if (INVERTED_KEYS.has(key)) return true;
    if (key.includes("expense") || key.includes("cost") || key.includes("cogs")) return true;
    return false;
}

function deltaColor(value: number, key: string): string {
    if (value === 0) return "text-muted-foreground";
    const positive = value > 0;
    const good = isUpBad(key) ? !positive : positive;
    return good ? "text-green-600" : "text-red-600";
}

function KpiCard({card}: {card: XeroPnLKpiCard}) {
    const isPctCard = card.key.endsWith("_pct") || card.key === "opex_ratio";
    return (
        <Card className="rounded-2xl border border-[#E2E8F0] shadow-sm">
            <CardContent className="p-5 text-center">
                <p className="text-2xl font-extrabold tabular-nums text-[#209DB3]">
                    {formatValue(card)}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">
                    {card.label}
                </p>
                {(card.delta_pct != null || card.delta != null) && (
                    <div className="flex flex-col items-center gap-0.5 mt-2">
                        {card.delta_pct != null && (
                            <div className="flex items-center justify-center gap-1">
                                {card.delta_pct > 0 && <ArrowUpIcon className={`size-3 ${deltaColor(card.delta_pct, card.key)}`} />}
                                {card.delta_pct < 0 && <ArrowDownIcon className={`size-3 ${deltaColor(card.delta_pct, card.key)}`} />}
                                <span className={`text-xs font-medium ${deltaColor(card.delta_pct, card.key)}`}>
                                    {card.delta_pct > 0 ? "+" : ""}{card.delta_pct}% {card.vs_label}
                                </span>
                            </div>
                        )}
                        {card.delta != null && !isPctCard && (
                            <span className={`text-[11px] font-medium tabular-nums ${deltaColor(card.delta, card.key)}`}>
                                {formatDollar(card.delta)}
                            </span>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

interface XeroPnlKpiCardsProps {
    cards: XeroPnLKpiCard[];
}

export function XeroPnlKpiCards({cards}: XeroPnlKpiCardsProps) {
    return (
        <div className="space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3] mb-3">
                Key Performance Indicators
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {cards.map((c) => (
                    <KpiCard key={c.key} card={c} />
                ))}
            </div>
        </div>
    );
}
